import type { ReactNode } from "react";
import { Button } from "../../client/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import { GripVertical, Lock, X } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { WIDGET_REGISTRY, isWidgetLocked } from "./registry";
import type { WidgetKey } from "./types";
import ResizeHandle from "./ResizeHandle";
import type { HandlePosition } from "./useWidgetResize";
import { useWidgetData } from "./WidgetDataContext";

export interface WidgetFrameProps {
  widgetKey: WidgetKey;
  currentColSpan: number;
  currentRowSpan: number;
  gridCols: number;
  isEditMode: boolean;
  isMobile: boolean;
  onSizeChange: (colSpan: number, rowSpan: number) => void;
  onRemove: () => void;
  dragHandleProps?: Record<string, any>;
  onResizeStart?: (e: React.PointerEvent<HTMLElement>, handle: HandlePosition) => void;
  activeResizeHandle?: HandlePosition | null;
  children: ReactNode;
}

function camelize(key: string): string {
  return key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

export default function WidgetFrame({
  widgetKey,
  currentColSpan,
  currentRowSpan,
  gridCols,
  isEditMode,
  isMobile,
  onSizeChange,
  onRemove,
  dragHandleProps,
  onResizeStart,
  activeResizeHandle,
  children,
}: WidgetFrameProps) {
  const t = useTranslation();
  const { isFreePlan, onOpenPlanModal } = useWidgetData();
  const reg = WIDGET_REGISTRY[widgetKey];
  const widgetName = t.dashboard.widgets[camelize(widgetKey) as keyof typeof t.dashboard.widgets];
  const locked = isWidgetLocked(widgetKey, isFreePlan);

  const maxColForRow = Math.min(reg.maxColSpan, gridCols);
  const sizeOptions: Array<{ value: string; label: string }> = [];
  for (let c = reg.minColSpan; c <= maxColForRow; c++) {
    for (let r = reg.minRowSpan; r <= reg.maxRowSpan; r++) {
      sizeOptions.push({ value: `${c}x${r}`, label: `${c}×${r}` });
    }
  }

  const canResizeCol = reg.maxColSpan > reg.minColSpan;
  const canResizeRow = reg.maxRowSpan > reg.minRowSpan;
  const showHandles = isEditMode && !isMobile && !!onResizeStart;

  if (!isEditMode) {
    if (locked) {
      return (
        <div className="relative h-full w-full overflow-hidden rounded-xl">
          <div className="blur-sm opacity-40 pointer-events-none h-full w-full" aria-hidden="true">
            {children}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/60 backdrop-blur-sm rounded-xl">
            <Lock className="h-6 w-6 text-primary" />
            <Button size="sm" onClick={onOpenPlanModal}>
              {t.account.upgradeToPro}
            </Button>
          </div>
        </div>
      );
    }
    // `min-w-0` + `overflow-hidden` : sur une carte étroite, un contenu trop
    // large est tronqué proprement au lieu de déborder par-dessus les widgets
    // voisins (chiffres coupés, montants qui se chevauchent).
    return <div className="h-full w-full min-w-0 overflow-hidden">{children}</div>;
  }

  return (
    <div className="relative group rounded-[10px] ring-2 ring-primary/40 ring-dashed h-full overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between gap-2 px-2 py-1 bg-background/70 backdrop-blur-sm rounded-t-[10px] border-b border-primary/30">
        <button
          type="button"
          aria-label={`Drag ${widgetName}`}
          {...(dragHandleProps ?? {})}
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        <div className="flex items-center gap-2">
          {sizeOptions.length > 1 && (
            <Select
              value={`${currentColSpan}x${currentRowSpan}`}
              onValueChange={(v) => {
                const [c, r] = v.split("x").map(Number);
                onSizeChange(c, r);
              }}
            >
              <SelectTrigger className="h-7 w-20 text-xs" aria-label={t.dashboard.widgetSizeLabel}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sizeOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-destructive"
            onClick={onRemove}
            aria-label={t.dashboard.removeWidget}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className={`h-full w-full overflow-hidden${locked ? " blur-sm opacity-40 pointer-events-none" : ""}`}>
        {children}
      </div>

      {!locked && showHandles && canResizeCol && (
        <ResizeHandle
          position="right"
          ariaLabel={t.dashboard.resizeWidgetRight}
          isResizing={activeResizeHandle === "right" || activeResizeHandle === "corner"}
          onPointerDown={(e) => onResizeStart!(e, "right")}
        />
      )}
      {!locked && showHandles && canResizeRow && (
        <ResizeHandle
          position="bottom"
          ariaLabel={t.dashboard.resizeWidgetBottom}
          isResizing={activeResizeHandle === "bottom" || activeResizeHandle === "corner"}
          onPointerDown={(e) => onResizeStart!(e, "bottom")}
        />
      )}
      {!locked && showHandles && (canResizeCol || canResizeRow) && (
        <ResizeHandle
          position="corner"
          ariaLabel={t.dashboard.resizeWidgetCorner}
          isResizing={activeResizeHandle === "corner"}
          onPointerDown={(e) => onResizeStart!(e, "corner")}
        />
      )}
    </div>
  );
}
